/**
 * Admin Dashboard Component
 *
 * Dashboard view for admin users with comprehensive stats and controls
 *
 * @module pages/dashboard/AdminDashboard
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Container, Row, Col, Card, Button, Badge, Alert, Breadcrumb } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { getProjectsSummary, getProjectsWithBudgetAlerts } from '../../services/projectService';
import { listQuotations } from '../../services/quotationService';
import { getInventorySummary } from '../../services/inventoryService';
import { supabase } from '../../services/supabase';
import StatCard from '../../components/common/StatCard';
import BudgetOverviewWidget from '../../components/dashboard/BudgetOverviewWidget';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useKaneAssistant } from '../../hooks/useKaneAssistant';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const openKaneAssistant = useKaneAssistant();

  // Fetch dashboard data
  //
  // All queries below are gated on `enabled: !!profile?.id` and keyed by that id.
  // Two related fixes here (#260):
  //   1. Without `enabled`, a query could fire (and, on a transient RLS/session
  //      hiccup, resolve to empty data) before the auth session was fully attached,
  //      then sit cached as "valid empty" for the default staleTime, showing 0s
  //      until a hard reload cleared the client-side cache.
  //   2. Keying by profile.id (instead of a static string) means switching users
  //      can't silently reuse another user's cached dashboard numbers.
  // Card rendering also now distinguishes "loading" from "actually zero" (see
  // `loading={isLoading}` below and the StatCard loading prop) instead of
  // defaulting straight to 0 while data is in flight.
  const {
    data: projectsData,
    isLoading: isProjectsLoading,
  } = useQuery({
    queryKey: ['dashboard-projects-summary', profile?.id],
    queryFn: getProjectsSummary,
    enabled: !!profile?.id,
  });

  const {
    data: budgetAlertsData,
    isLoading: isBudgetAlertsLoading,
  } = useQuery({
    queryKey: ['dashboard-budget-alerts', profile?.id],
    queryFn: () => getProjectsWithBudgetAlerts(80),
    enabled: !!profile?.id,
  });

  // `getInventorySummary` RESUELVE con `{ data, error }` en vez de tirar, así
  // que `isError` nunca se encendía: un fallo llegaba como `data.data ===
  // undefined` y la tarjeta se quedaba con el spinner "Cargando datos..."
  // para siempre. Se re-lanza acá para que react-query lo trate como lo que
  // es y la tarjeta pueda ofrecer "Reintentar".
  const {
    data: inventoryData,
    isLoading: isInventoryLoading,
    isError: isInventoryError,
    error: inventoryError,
    refetch: refetchInventory,
  } = useQuery({
    queryKey: ['dashboard-inventory-summary', profile?.id],
    queryFn: async () => {
      const result = await getInventorySummary();
      if (result?.error) throw result.error;
      return result;
    },
    enabled: !!profile?.id,
  });

  const {
    data: pendingQuotationsData,
    isLoading: isPendingQuotationsLoading,
  } = useQuery({
    queryKey: ['dashboard-pending-quotations', profile?.id],
    queryFn: () => listQuotations({ status: 'under_review', pageSize: 1 }),
    enabled: !!profile?.id,
  });

  const {
    data: invoiceSummary,
    isLoading: isInvoiceLoading,
    isError: isInvoiceError,
    error: invoiceError,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ['dashboard-invoice-summary', profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_summary')
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id,
  });

  // El throw de arriba SÍ existía, pero nadie miraba `isError`: las cuatro
  // StatCards leían `invoiceSummary?.total_* || 0` y pintaban ₡0 con toda
  // confianza. ₡0 facturado es un dato de negocio, no un error de red — el
  // panel no puede decir uno cuando pasó el otro.
  if (isInvoiceError) {
    // eslint-disable-next-line no-console
    console.error('[AdminDashboard] invoice_summary falló', invoiceError);
  }
  if (isInventoryError) {
    // eslint-disable-next-line no-console
    console.error('[AdminDashboard] getInventorySummary falló', inventoryError);
  }

  const projectsSummary = projectsData?.data;
  const budgetAlerts = budgetAlertsData?.data || [];
  const inventorySummary = inventoryData?.data;

  const totalBilled = Number(invoiceSummary?.total_billed || 0);
  const totalPaid = Number(invoiceSummary?.total_paid || 0);
  const totalOpen = Number(invoiceSummary?.total_open || 0);
  const totalOverdue = Number(invoiceSummary?.total_overdue || 0);
  const paidPercentage = totalBilled > 0 ? (totalPaid / totalBilled) * 100 : 0;

  const totalProjects = projectsSummary?.total || 0;
  const activeProjects = projectsSummary?.byStatus?.in_progress || 0;
  const pendingQuotations = pendingQuotationsData?.count || 0;
  const budgetAlertCount = budgetAlerts.length;

  return (
    <DashboardLayout>
      <Container fluid className="admin-dashboard">
        {/* Header with Breadcrumbs and Quick Actions */}
        <Row className="mb-4 align-items-center">
        <Col lg={8}>
          <Breadcrumb className="mb-2">
            <Breadcrumb.Item active>
              <i className="bi bi-house-door me-1"></i>
              Dashboard
            </Breadcrumb.Item>
          </Breadcrumb>
          <h1 className="h2 mb-1 fw-bold">Panel de Administración</h1>
          <p className="text-muted mb-0">
            <i className="bi bi-person-circle me-1"></i>
            Bienvenido, {profile?.full_name || 'Administrador'}
          </p>
        </Col>
        <Col lg={4} className="text-lg-end mt-3 mt-lg-0">
        </Col>
      </Row>

      {/* Stats Cards - Enhanced with animations */}
      <Row className="g-4 mb-4">
        <Col md={6} lg={3}>
          <div className="stat-card-wrapper">
            <StatCard
              title="Proyectos Totales"
              value={totalProjects}
              loading={isProjectsLoading}
              icon="bi-folder"
              color="primary"
              onClick={() => navigate('/proyectos')}
              data-testid="dashboard-stat-total-projects"
            />
          </div>
        </Col>

        <Col md={6} lg={3}>
          <div className="stat-card-wrapper">
            <StatCard
              title="Proyectos Activos"
              value={activeProjects}
              loading={isProjectsLoading}
              icon="bi-folder-check"
              color="success"
              // `?status=active` era doblemente falso: `active` no existe en
              // `projects_status_check` (draft | quotation_sent |
              // quotation_accepted | po_received | in_progress | closed) y
              // ProjectsUnifiedView ni siquiera lee `status` de la URL — sólo
              // `client` y `tab`. La tarjeta decía N y el clic mostraba los 71
              // proyectos sin filtrar. El valor ahora es el mismo que alimenta
              // el número (`byStatus.in_progress`), así que cuando
              // ProjectsUnifiedView lea el parámetro, tarjeta y destino van a
              // coincidir sin tocar esto de nuevo.
              onClick={() => navigate('/proyectos?status=in_progress')}
              data-testid="dashboard-stat-active-projects"
            />
          </div>
        </Col>

        <Col md={6} lg={3}>
          <div className="stat-card-wrapper">
            <StatCard
              title="Alertas de Presupuesto"
              value={budgetAlertCount}
              loading={isBudgetAlertsLoading}
              icon="bi-exclamation-triangle"
              color={budgetAlertCount > 0 ? 'warning' : 'secondary'}
              data-testid="dashboard-stat-budget-alerts"
            />
          </div>
        </Col>

        <Col md={6} lg={3}>
          <div className="stat-card-wrapper">
            <StatCard
              title="Cotizaciones Pendientes"
              value={pendingQuotations}
              loading={isPendingQuotationsLoading}
              icon="bi-file-text"
              color="info"
              // El número lo da `listQuotations({status:'under_review'})` —
              // COTIZACIONES. El clic llevaba a la lista de TAREAS filtrada por
              // `category=cotizacion`, categoría que no existe (la real es
              // `quotation_review`), así que la tarjeta decía 3 y el destino
              // decía "No se encontraron tareas": entidad equivocada y valor
              // equivocado. Ahora va a la lista de cotizaciones con el mismo
              // estado que se contó.
              onClick={() => navigate('/cotizaciones?status=under_review')}
              data-testid="dashboard-stat-pending-quotations"
            />
          </div>
        </Col>
      </Row>

      {/* Budget Overview Widget - Replaces old alerts widget */}
      <Row className="mb-4">
        <Col>
          <BudgetOverviewWidget limit={5} autoRefreshInterval={300000} />
        </Col>
      </Row>

      {/* Invoice Metrics */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-gradient-light d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-semibold">
                <i className="bi bi-receipt-cutoff me-2 text-primary"></i>
                Facturacion
              </h5>
              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={() => navigate('/facturas')}
              >
                Ver facturas <i className="bi bi-arrow-right ms-1"></i>
              </Button>
            </Card.Header>
            <Card.Body>
              {isInvoiceError ? (
                <Alert variant="warning" className="mb-0" data-testid="dashboard-invoice-error">
                  <Alert.Heading className="h6 mb-1">
                    <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                    No se pudieron cargar los montos de facturación
                  </Alert.Heading>
                  <p className="mb-2 small">
                    Los totales de abajo quedarían en cero sin que eso sea real. Volvé a intentarlo.
                  </p>
                  <Button variant="outline-warning" size="sm" onClick={() => refetchInvoices()}>
                    <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                    Reintentar
                  </Button>
                </Alert>
              ) : (
              <Row className="g-3">
                <Col xs={12} sm={6} lg={3}>
                  <StatCard
                    title="Total Facturado"
                    value={formatCurrency(totalBilled)}
                    loading={isInvoiceLoading}
                    icon="bi-cash-stack"
                    color="primary"
                    onClick={() => navigate('/facturas')}
                  />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <StatCard
                    title="Pagado"
                    value={formatCurrency(totalPaid)}
                    loading={isInvoiceLoading}
                    icon="bi-check-circle"
                    color="success"
                    trend={{
                      direction: paidPercentage >= 50 ? 'up' : 'neutral',
                      value: `${formatPercentage(paidPercentage, 0)} del total`,
                    }}
                    onClick={() => navigate('/facturas?status=paid')}
                  />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <StatCard
                    title="Por Cobrar"
                    value={formatCurrency(totalOpen)}
                    loading={isInvoiceLoading}
                    icon="bi-hourglass-split"
                    color={totalOpen > 0 ? 'warning' : 'secondary'}
                    onClick={() => navigate('/facturas')}
                  />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <StatCard
                    title="Vencido"
                    value={formatCurrency(totalOverdue)}
                    loading={isInvoiceLoading}
                    icon="bi-exclamation-triangle"
                    color={totalOverdue > 0 ? 'danger' : 'secondary'}
                    onClick={() => navigate('/facturas?status=overdue')}
                  />
                </Col>
              </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Inventory Summary & Quick Actions */}
      <Row className="g-4">
        <Col lg={6}>
          <Card className="h-100 shadow-sm hover-lift">
            <Card.Header className="bg-gradient-light">
              <h5 className="mb-0 fw-semibold">
                <i className="bi bi-box-seam me-2 text-primary"></i>
                Resumen de Inventario
              </h5>
            </Card.Header>
            <Card.Body>
              {inventorySummary ? (
                <div className="inventory-summary">
                  <div className="inventory-stat-row">
                    <span className="inventory-label">
                      <i className="bi bi-archive me-2"></i>
                      Total de Artículos:
                    </span>
                    <strong className="inventory-value">{inventorySummary.totalItems}</strong>
                  </div>
                  <div className="inventory-stat-row">
                    <span className="inventory-label">
                      <i className="bi bi-exclamation-circle me-2"></i>
                      Artículos Bajo Stock:
                    </span>
                    <Badge bg="warning" className="text-dark px-3 py-2">
                      {inventorySummary.lowStockItems}
                    </Badge>
                  </div>
                  <div className="inventory-stat-row">
                    <span className="inventory-label">
                      <i className="bi bi-x-circle me-2"></i>
                      Artículos Agotados:
                    </span>
                    <Badge bg="danger" className="px-3 py-2">
                      {inventorySummary.outOfStockItems}
                    </Badge>
                  </div>
                  <div className="inventory-stat-row">
                    <span className="inventory-label">
                      <i className="bi bi-currency-dollar me-2"></i>
                      Valor Total:
                    </span>
                    <strong className="inventory-value text-primary fs-5">
                      {formatCurrency(inventorySummary.totalValue)}
                    </strong>
                  </div>
                  <div className="mt-4">
                    <Button
                      variant="outline-primary"
                      className="w-100 btn-hover-primary"
                      onClick={() => navigate('/inventario')}
                    >
                      <i className="bi bi-arrow-right me-2"></i>
                      Ver Inventario Completo
                    </Button>
                  </div>
                </div>
              ) : isInventoryError ? (
                /* Antes: el mismo spinner "Cargando datos..." tanto para
                   "todavía viene" como para "falló", así que un error dejaba
                   girando la tarjeta hasta que el usuario recargaba. */
                <div className="text-center py-4" data-testid="dashboard-inventory-error">
                  <i className="bi bi-exclamation-triangle fs-1 text-warning" aria-hidden="true"></i>
                  <h6 className="mt-3">No se pudo cargar el inventario</h6>
                  <p className="text-muted small">
                    Es un problema de conexión con el servidor, no un inventario vacío.
                  </p>
                  <Button variant="outline-primary" size="sm" onClick={() => refetchInventory()}>
                    <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                    Reintentar
                  </Button>
                </div>
              ) : isInventoryLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="text-muted mb-0">Cargando datos...</p>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-box-seam fs-1 d-block mb-2" aria-hidden="true"></i>
                  <p className="mb-0">No hay artículos en el inventario todavía.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100 shadow-sm hover-lift">
            <Card.Header className="bg-gradient-light">
              <h5 className="mb-0 fw-semibold">
                <i className="bi bi-lightning-fill me-2 text-warning"></i>
                Acciones Rápidas
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-3">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/cotizaciones/nueva')}
                  className="quick-action-secondary"
                >
                  <i className="bi bi-file-text me-2"></i>
                  Nueva Cotización
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/gastos/nuevo')}
                  className="quick-action-secondary"
                >
                  <i className="bi bi-receipt me-2"></i>
                  Registrar Gasto
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/progreso')}
                  className="quick-action-secondary"
                >
                  <i className="bi bi-upload me-2"></i>
                  Subir Fotos de Progreso
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/inventario')}
                  className="quick-action-secondary"
                >
                  <i className="bi bi-box me-2"></i>
                  Gestionar Inventario
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/proyectos')}
                  className="quick-action-secondary"
                >
                  <i className="bi bi-folder me-2"></i>
                  Ver Todos los Proyectos
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Help Card — Enhanced.
          El copy mandaba a "el botón del chat en la esquina inferior derecha":
          ese chat in-app se eliminó en #174 y hoy la entrada es el ícono del
          robot arriba a la derecha. Mandar a apretar un botón inexistente es
          peor que no decir nada, así que además de corregir el texto el aviso
          abre el asistente él mismo: un <Button> real (tap target, foco de
          teclado y nombre accesible), no una instrucción para buscar otro
          control. */}
      <Row className="mt-4 mb-4">
        <Col>
          <Alert variant="info" className="shadow-sm border-0 bg-gradient-info">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <div className="alert-icon">
                <i className="bi bi-robot fs-1 text-info" aria-hidden="true"></i>
              </div>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <Alert.Heading className="h5 mb-2 fw-bold">
                  <i className="bi bi-lightbulb me-2" aria-hidden="true"></i>
                  Asistente AI Disponible
                </Alert.Heading>
                <p className="mb-0">
                  Gestioná proyectos, cotizaciones, gastos y más escribiéndolo en
                  lenguaje natural. Abrilo desde acá o con el ícono del robot
                  arriba a la derecha.
                </p>
              </div>
              <Button
                variant="primary"
                onClick={openKaneAssistant}
                data-testid="admin-dashboard-open-assistant"
              >
                <i className="bi bi-robot me-2" aria-hidden="true"></i>
                Abrir Asistente
              </Button>
            </div>
          </Alert>
        </Col>
      </Row>
      </Container>
    </DashboardLayout>
  );
};

export default AdminDashboard;
